import {UI} from "../../../../stem-core/src/ui/UIBase";

import {PaywallPage, PaywallPageStyle} from "../../PaywallPage";
import {registerStyle} from "../../../../stem-core/src/ui/style/Theme";
import {styleRule, styleRuleInherit} from "../../../../stem-core/src/decorators/Style";
import {iFrameState} from "../../../services/IFrameState";
import {GATE_TYPE, PANEL_FRAME, TNR_ALIAS} from "../../../../blink-sdk/Constants";
import {isSmallScreen} from "../../../UtilsLib";
import {MerchantLogo} from "../../../common/MerchantLogo";
import {iFrameMerchantService} from "../../../services/IframeMerchantService";
import {panelChildMargin} from "../../../StyleConstants";


export class BasePanelPageStyle extends PaywallPageStyle {
    paddingFunction = () => iFrameState.gateType === GATE_TYPE.popup ?
        (isSmallScreen() ? "12px 18px 24px" : "18px 36px 36px") : "18px 14px";

    // TODO: This should not be in the style sheet
    shouldDisplayFrame = () => iFrameState.gateType !== GATE_TYPE.banner && iFrameState.frame === PANEL_FRAME.logo;

    containerStyle = {
        justifyContent: "space-between",
        ">:not(:last-child, :first-child)": {
            marginBottom: panelChildMargin,
        },
    };

    @styleRuleInherit
    paywallPage = {
        border: () => "1px solid " + this.themeProps.MERCHANT_FRAME_BORDER_COLOR,
        background: this.themeProps.MERCHANT_FRAME_BACKGROUND,
        borderRadius: this.themeProps.MERCHANT_FRAME_BORDER_RADIUS,
        display: "flex",
        flexDirection: "column",
        ...(this.shouldDisplayFrame() ? {
            overflow: "hidden",
        } : {
            overflow: ["overlay", "auto"],
            padding: this.paddingFunction,
            ...this.containerStyle,
        })
    };

    @styleRule
    header = {
        width: "100%",
        background: this.themeProps.MERCHANT_FRAME_HEADER_BACKGROUND,
        padding: 12,
        borderBottom: () => "0.5px solid " + this.themeProps.MERCHANT_FRAME_HEADER_BORDER_COLOR,
    };

    @styleRule
    body = {
        width: "100%",
        overflow: ["overlay", "auto"],
        flex: 1,
        padding: this.paddingFunction,
        display: "flex",
        flexDirection: "column",
        ...this.containerStyle,
    };

    @styleRule
    button = {
        margin: "auto",
        marginTop: 10,
        width: "100%",
        maxWidth: isSmallScreen() ? "" : 368,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_LARGE,
    };

    @styleRuleInherit
    paywallPagePopup = {
        padding: 0
    };

    @styleRule
    title = this.themeProps.PAYWALL_TITLE;

    @styleRule
    secondaryButton = this.themeProps.TERTIARY_BUTTON;

    @styleRule
    link = this.themeProps.PAYWALL_LINK;

    @styleRule
    center = {
        width: "100%",
        textAlign: "center",
    };

    @styleRule
    scrollFade = {
        boxShadow: "0 12px 12px 0px rgb(0 0 0 / 12%) inset, 0 -12px 12px 0px rgb(0 0 0 / 12%) inset",
    };

    @styleRule
    scrollTopFade = {
        boxShadow: "0 12px 12px 0px rgb(0 0 0 / 12%) inset",
    };

    @styleRule
    scrollBottomFade = {
        boxShadow: "0 -12px 12px 0px rgb(0 0 0 / 12%) inset",
    };

    @styleRule
    leftAlign = {
        textAlign: "left !important",
    };

    @styleRule
    shadowScrollScrim = {
        position: "absolute",
        width: "100%",
        bottom: 0,
        left: 0,
        pointerEvents: "none",
        zIndex: 2
    };
}
// TODO(@Denis): Fix panels being to big because of that bottom shadow stuff
@registerStyle(BasePanelPageStyle)
export class BasePanelPage extends PaywallPage {
    widthValue = 440;
    scrollListener = null;
    shadowScrollClass = "";
    shadowScrollStyle = {};

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            title: "",
        };
    }

    handleScroll() {
        try {
            this.scrollListener?.remove();
        } catch (e) {
            // Somehow magically this scroll listener gets removed.
        }
        const applyScrollStyle = element => {
            if (!element) {
                return; // Some weird redraws occurred.
            }
            this.shadowScrollScrim.removeClass(this.styleSheet.scrollTopFade);
            this.shadowScrollScrim.removeClass(this.styleSheet.scrollBottomFade);
            this.shadowScrollScrim.removeClass(this.styleSheet.scrollFade);
            if (element.node.scrollHeight > element.node.clientHeight) {
                if (element.node.scrollTop && element.node.scrollTop + element.node.clientHeight < element.node.scrollHeight) {
                    this.shadowScrollClass = this.styleSheet.scrollFade;
                } else if (element.node.scrollTop) {
                    this.shadowScrollClass = this.styleSheet.scrollTopFade;
                } else if (element.node.scrollTop + element.node.clientHeight < element.node.scrollHeight) {
                    this.shadowScrollClass = this.styleSheet.scrollBottomFade;
                }
            } else {
                this.shadowScrollClass = "";
            }
            this.shadowScrollStyle = {
                height: `calc(100% - ${this.header ? this.header.getHeight() : 0}px)`,
            };
            this.shadowScrollScrim.setStyle(this.shadowScrollStyle);
            this.shadowScrollScrim.addClass(this.shadowScrollClass);
        };
        if (this.styleSheet.shouldDisplayFrame() && this.body && this.header && this.shadowScrollScrim) {
            applyScrollStyle(this.body);
            this.scrollListener = this.body.addNodeListener("scroll", () => applyScrollStyle(this.body));
        } else if (this.shadowScrollScrim) {
            applyScrollStyle(this);
            this.scrollListener = this.addNodeListener("scroll", () => applyScrollStyle(this));
        }
    }

    getChildrenToRender() {
        this.attachTimeout(() => this.handleScroll());

        const {styleSheet} = this;
        const title = typeof this.options.title === "function" ? this.options.title() : this.options.title;
        const titleElement = title ? <div className={styleSheet.title + (iFrameMerchantService.isMerchant(TNR_ALIAS) ? styleSheet.leftAlign : "")}>{title}</div> : null;

        if (!styleSheet.shouldDisplayFrame()) {
            return [<div ref="shadowScrollScrim" className={styleSheet.shadowScrollScrim + this.shadowScrollClass}
                         style={this.shadowScrollStyle}/>,
                titleElement, this.render()];
        }
        return [
            <div className={styleSheet.header} ref="header">
                <MerchantLogo />
            </div>,
            <div className={styleSheet.body} ref="body">
                <div ref="shadowScrollScrim" className={styleSheet.shadowScrollScrim + this.shadowScrollClass}
                     style={this.shadowScrollStyle}/>
                {titleElement}
                {this.render()}
            </div>
        ];
    }
}
